import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import type { HTMLAttributes, ReactNode, Ref } from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

const LISTBOX_PADDING = 8;

const OptionText = styled(Typography, { name: 'OptionText' })({
  display: 'block',
});

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const rowData = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  const [rowProps, rowLabel] = rowData;
  return (
    <li {...rowProps} key={rowProps.key} style={inlineStyle}>
      <OptionText variant="body2" noWrap>
        {rowLabel}
      </OptionText>
    </li>
  );
}

function useResetCache(data: any) {
  const ref = useRef<FixedSizeList>(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.scrollTo(0);
    }
  }, [data]);
  return ref;
}

const ITEM_HEIGHT = 32;

type OuterElementProps = HTMLAttributes<HTMLDivElement> & {
  ref?: Ref<HTMLDivElement>;
};

const OuterElementPropsContext = createContext<HTMLAttributes<HTMLElement>>({});

const OuterElement = ({ ref, ...props }: OuterElementProps) => {
  const outerProps = useContext(OuterElementPropsContext);
  return <div {...props} {...outerProps} ref={ref} />;
};

const InnerElement = ({
  ref,
  style,
  ...props
}: HTMLAttributes<HTMLUListElement> & { ref?: Ref<HTMLUListElement> }) => (
  <ul {...props} ref={ref} style={{ ...style, margin: 0, padding: 0 }} />
);

const ListboxComponentVirtualized = ({
  children,
  ref,
  ...other
}: HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLDivElement> }) => {
  const itemData: ReactNode[] = [];
  (children as Array<ReactNode & { children?: ReactNode[] }>).forEach(
    (item) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    }
  );

  const itemCount = itemData.length;
  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * ITEM_HEIGHT;
    }
    return itemCount * ITEM_HEIGHT;
  };
  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementPropsContext.Provider value={other}>
        <FixedSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          outerElementType={OuterElement}
          innerElementType={InnerElement}
          itemSize={ITEM_HEIGHT}
          overscanCount={10}
          itemCount={itemCount}
          ref={gridRef}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementPropsContext.Provider>
    </div>
  );
};

function VirtualizedAutocomplete<
  Value,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  ref,
  ...props
}: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo> & {
  ref?: Ref<HTMLDivElement>;
}) {
  const { getOptionLabel } = props;

  return (
    <MuiAutocomplete
      {...props}
      // @ts-expect-error
      renderOption={(props, option) => [props, getOptionLabel?.(option)]}
      slotProps={{
        listbox: {
          component: ListboxComponentVirtualized,
        },
      }}
      ref={ref}
    />
  );
}

export default VirtualizedAutocomplete;
